import { Typography } from '@mui/material';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { DISCOUNT_MAPPING, TRANSACTION_FEE } from '../../../constants/policy';
import { formatCurrency } from '../../../shared/utils/currencyUtils';
import { isPetPlanInCancelledStatus } from '../../../shared/utils/PetPlanUtils';
import {
  formatDiscountPercentage,
  getFullPercentage,
} from '../../../shared/utils/petUtils';
import { midtermSummaryStyle } from './midterm-summary-style';

function MidtermSummary(props) {
  const classes = midtermSummaryStyle();

  const {
    showSubTotal = true,
    showGrandTotal = true,
    showTransactionFee = true,
    petData,
    midtermPolicyData = { latest_pet_policy: { annual_limit: '' } },
  } = props;

  const annualLimit = formatCurrency(
    midtermPolicyData?.latest_pet_policy?.annual_limit
      .slice(0, -3)
      .replace(/,/g, ''),
    {
      areCents: false,
      hideZero: true,
      ifNaN: 'Unlimited',
    }
  );

  const isChargedAnnually = !!petData?.latest_pet_policy.is_annualized_policy;
  const months = isChargedAnnually ? 12 : 1;

  const policyFiledRate =
    (midtermPolicyData?.latest_pet_policy?.filed_rate || 0) * months;
  const policyRawRate =
    (midtermPolicyData?.latest_pet_policy?.raw_rate || 0) * months;

  const petPlanCost = !isPetPlanInCancelledStatus(petData?.latest_pet_plan?.status)
    ? (petData?.latest_pet_plan?.cost || 0) * months
    : 0;
  const transactionFee = isChargedAnnually ? 0 : TRANSACTION_FEE;
  const subtotal = policyFiledRate + petPlanCost;
  const grandTotal = subtotal + transactionFee;

  const hasDiscount = DISCOUNT_MAPPING.some(
    discount => !!midtermPolicyData?.latest_pet_policy?.[discount.key]
  );

  const subTotalFormatted = formatCurrency(subtotal, {
    areCents: false,
  });

  const grandTotalFormatted = formatCurrency(grandTotal, {
    areCents: false,
  });

  return (
    <div>
      <div className={classes.planBenefitsContainer}>
        <Typography className={classes.planHeader}>Pumpkin Pet insurance</Typography>
        <Typography
          className={classnames(classes.planHeader, {
            [classes.discountColor]: hasDiscount === true,
          })}
        >
          {`${formatCurrency(policyFiledRate, {
            areCents: false,
          })} ${isChargedAnnually ? '/ yr' : '/ mo'}`}
        </Typography>
      </div>
      <div>
        <ul className={classes.planBenefitsList}>
          <li>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography className={classes.planBenefits}>
                {`${getFullPercentage(
                  midtermPolicyData?.latest_pet_policy?.coinsurance
                )} Reimbursement Rate`}
              </Typography>
              {hasDiscount ? (
                <Typography className={classes.planBenefitsWithoutDiscount}>
                  {`${formatCurrency(policyRawRate, {
                    areCents: false,
                  })} ${isChargedAnnually ? '/ yr' : '/ mo'}`}
                </Typography>
              ) : null}
            </div>
          </li>
          <li>
            <Typography className={classes.planBenefits}>
              {`${formatCurrency(midtermPolicyData?.latest_pet_policy?.deductible, {
                areCents: false,
                hideZero: true,
                ifNaN: 'Unlimited',
              })} Deductible`}
            </Typography>
          </li>
          <li>
            <Typography className={classes.planBenefits}>
              {`${
                annualLimit === 'Unlimited' ? annualLimit : annualLimit + 'K'
              } Annual Limit`}
            </Typography>
          </li>
        </ul>

        {DISCOUNT_MAPPING.map(discount => {
          const discountValue = midtermPolicyData?.latest_pet_policy?.[discount.key];

          return discountValue ? (
            <Typography className={classes.planDiscount} key={discount.key}>
              {discount.label} {formatDiscountPercentage(discountValue)}%
            </Typography>
          ) : null;
        })}
      </div>

      {petData?.latest_pet_plan &&
      !isPetPlanInCancelledStatus(petData.latest_pet_plan.status) ? (
        <>
          <div className={classes.planBenefitsContainer}>
            <Typography className={classes.planHeader}>
              Preventive Essentials
            </Typography>
            <Typography className={classes.planHeader}>
              {`${formatCurrency(petPlanCost, {
                areCents: false,
              })} ${isChargedAnnually ? '/ yr' : '/ mo'}`}
            </Typography>
          </div>
          <div>
            <ul className={classes.planBenefitsList}>
              <li>
                <Typography className={classes.planBenefits}>
                  {`Pumpkin Prevent ${petData.pet_plan_version}`}
                </Typography>
              </li>
            </ul>
          </div>
        </>
      ) : null}

      {showSubTotal ? (
        <div className={classes.petPlanHeaderContainer}>
          <Typography className={classes.totalValues}>{`${
            isChargedAnnually ? 'Annual' : 'Monthly'
          } Subtotal:`}</Typography>
          <Typography className={classes.totalValues}>
            {subTotalFormatted}
          </Typography>
        </div>
      ) : null}

      {showGrandTotal ? (
        <div className={classes.petPlanHeaderContainer}>
          <Typography className={classes.totalValues}>
            {`${isChargedAnnually ? 'Annual' : 'Monthly'} Transaction Fee:`}
          </Typography>
          <Typography className={classes.totalValues}>
            {formatCurrency(transactionFee, { areCents: false })}
          </Typography>
        </div>
      ) : null}

      {showTransactionFee ? (
        <div className={classes.petPlanHeaderContainer}>
          <Typography className={classes.petPlanTitle}>{`${
            isChargedAnnually ? 'Annual' : 'Monthly'
          } Grand Total:`}</Typography>
          <Typography className={classes.petPlanTitle}>
            {grandTotalFormatted}
          </Typography>
        </div>
      ) : null}
    </div>
  );
}

MidtermSummary.propTypes = {
  showSubTotal: PropTypes.bool,
  showGrandTotal: PropTypes.bool,
  showTransactionFee: PropTypes.bool,
  petData: PropTypes.object.isRequired,
  midtermPolicyData: PropTypes.object.isRequired,
};

export default MidtermSummary;
